/**
 * Copy modal (Section 4 wireframe) — rename-before-land.
 *
 *  Make a copy
 *  From: <source name>
 *  New name [Copy of <name>...]
 *  Visibility (•) Private  ( ) Publish later
 *  ⓘ This copy is independent. Future changes to the original won't sync in.
 *  [Cancel]  [Create & open ▶]
 *
 * Phase 1 always creates the copy as PRIVATE (the "Publish later" choice is
 * UX honesty — the user just publishes from the new workflow's editor).
 */

import { useState } from 'react';
import { Button } from '../../ui/Button.js';
import { Field, Input } from '../../ui/Input.js';

export interface CopyChoice {
  name: string;
  startDisabled: boolean;
}

export function CopyModal({
  open,
  sourceName,
  isPending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  sourceName: string;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (choice: CopyChoice) => void;
}) {
  const [name, setName] = useState(`Copy of ${sourceName}`);
  const [publishLater, setPublishLater] = useState(true);

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-floating"
      >
        <h3 className="font-display text-lg font-bold text-brand-navy-800">
          Make a copy
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          From: <span className="font-medium text-brand-navy-700">{sourceName}</span>
        </p>

        <div className="mt-4 space-y-3">
          <Field label="New name">
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>

          <fieldset className="space-y-2 text-sm">
            <span className="field-label">Visibility</span>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                checked={publishLater}
                onChange={() => setPublishLater(true)}
                className="h-4 w-4 border-slate-300 text-brand-navy-600 focus:ring-brand-navy-300"
              />
              <span>Private (publish later)</span>
            </label>
          </fieldset>
        </div>

        <p className="mt-4 rounded-lg border border-brand-navy-100 bg-brand-navy-50 px-3 py-2 text-xs text-brand-navy-700">
          ⓘ This copy is independent. Future changes to the original won&apos;t sync in.
        </p>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="gold"
            size="sm"
            disabled={isPending || !name.trim()}
            onClick={() => onSubmit({ name: name.trim(), startDisabled: publishLater })}
          >
            {isPending ? 'Copying…' : 'Create & open ▶'}
          </Button>
        </div>
      </div>
    </div>
  );
}
